/**
 * User and Subscription Types
 *
 * Defines the structure of user data including subscription status and payment information
 *
 * Paddle is the only payment provider. The Lemon Squeezy columns were dropped from the
 * users table in 20251220_remove_lemon_columns.sql and are gone from here too.
 */

// ============================================================================
// SUBSCRIPTION TYPES
// ============================================================================

export type SubscriptionPlan = 'free' | 'pro' | 'team';

export interface SubscriptionStatus {
  /** Whether user has an active subscription */
  subscribed: boolean;
  /** Current subscription plan */
  subscription_plan: SubscriptionPlan;
  /** When the current subscription expires (UTC timestamp) */
  subscription_expires_at: string | null;
  /** Whether subscription is currently active and valid */
  isActive: boolean;
}

// ============================================================================
// PAYMENT PROVIDER INTEGRATION
// ============================================================================

export interface PaddleIntegration {
  /** Unique Paddle customer identifier */
  paddle_customer_id: string | null;
  /** Current Paddle subscription ID */
  paddle_subscription_id: string | null;
}

// ============================================================================
// VIDEO EDITOR
// ============================================================================

/**
 * The editor a user imports our generated XML into.
 *
 * Mirrors the CHECK constraint on public.users.video_editor and
 * public.conversions.video_editor. There is deliberately no 'other' member: every value
 * here has to map to a known import behaviour, and a user on an editor we have not
 * characterised is better represented by null (unknown, handled strictly) than by a
 * catch-all that reads as "answered".
 */
export type VideoEditor = 'premiere' | 'final_cut_pro' | 'davinci_resolve';

/** Display names. The stored value is the snake_case one - never send these to the API. */
export const VIDEO_EDITOR_LABELS: Record<VideoEditor, string> = {
  premiere: 'Adobe Premiere Pro',
  final_cut_pro: 'Final Cut Pro',
  davinci_resolve: 'DaVinci Resolve',
};

// ============================================================================
// USER PROFILE
// ============================================================================

export interface User {
  /** UUID from Supabase auth */
  id: string;
  /** UUID from Supabase auth (used for linking) */
  supabase_user_id: string | null;
  /** User's email address */
  email: string;
  /** User's display name */
  name: string | null;
  /** Avatar URL */
  avatar_url: string | null;
  /** Whether user is an admin */
  is_admin: boolean;
  /** Whether user is a super admin (system admin) */
  is_super_admin: boolean | null;
  /** Whether welcome page has been shown */
  welcome_page: boolean;

  /**
   * Which editor this user cuts in, or null if we have never asked / they dismissed it.
   *
   * null is NOT a fourth editor and must never be rendered as a selected option. Read it
   * through requiresStrictMediaReference() rather than comparing it yourself - an unknown
   * editor has to be treated as strictly as DaVinci Resolve, and every place that forgets
   * that is a place a user can reach a frozen import.
   */
  video_editor: VideoEditor | null;

  /**
   * When this user confirmed they had read the DaVinci Resolve media-path requirement,
   * or null if they never have - including for users who are not on Resolve at all, so
   * never read it without reading video_editor too.
   *
   * Server-stamped. PATCH /rest/v1/user/profile takes a boolean
   * (`resolve_warning_acknowledged`) and sets the clock itself; the column is not
   * client-writable. Cleared automatically whenever video_editor changes, so a user who
   * leaves Resolve and comes back is asked again rather than inheriting a stale yes.
   */
  resolve_warning_acknowledged_at: string | null;

  // Subscription fields
  //
  // subscribed/subscription_plan/subscription_expires_at mirror Paddle's webhook state
  // directly and can lag reality: a lapsed or paused subscription can sit at
  // subscribed=true/plan=pro until the next webhook or cron sweep touches the row.
  // is_subscription_active/effective_subscription_plan are computed at request time
  // (see isSubscriptionActive/getEffectivePlan below) and are what actually decides
  // access - read those two for "is this user subscribed right now", not the raw ones.
  subscribed: boolean;
  subscription_plan: SubscriptionPlan;
  subscription_expires_at: string | null;
  /** Computed, not stored: whether the subscription is valid right now. */
  is_subscription_active: boolean;
  /** Computed, not stored: the plan tier that actually applies right now. */
  effective_subscription_plan: SubscriptionPlan;

  /**
   * False for accounts provisioned from a Paddle checkout that have never chosen a
   * password. Put the set-password screen in front of them while it is false; see
   * signup-api.ts for the flow it belongs to.
   */
  password_set: boolean;

  // Payment integrations
  paddle_customer_id: string | null;
  paddle_subscription_id: string | null;

  // Timestamps
  created_at: string;
  updated_at: string;
  last_sign_in_at: string | null;
}

export interface UserSubscriptionData extends SubscriptionStatus, PaddleIntegration {
  user_id: string;
}

// ============================================================================
// SUBSCRIPTION HELPERS
// ============================================================================

/**
 * Determines if a user's subscription is currently active.
 *
 * Mirrors isSubscriptionActive() in main-api-worker/index.js exactly - keep the two in
 * sync, since this is a duplicate by necessity (the backend is what actually enforces
 * it; this copy exists so the frontend can reason about the same fields it's sent).
 * Prefer reading User.is_subscription_active from an API response over calling this
 * yourself when you have that response in hand - it's the same computation, already done.
 *
 * A subscription is active if:
 * 1. subscribed is true
 * 2. the plan is 'free' (free tier has nothing to expire), OR
 *    subscription_expires_at is set and in the future
 *
 * @param user User object with subscription fields
 * @returns true if subscription is currently active
 */
export function isSubscriptionActive(user: Partial<User>): boolean {
  if (!user.subscribed) {
    return false;
  }

  if (user.subscription_plan === 'free') {
    return true;
  }

  if (!user.subscription_expires_at) {
    // A paid plan with no expiration date is treated as inactive rather than perpetual -
    // this shouldn't happen in practice, and defaulting to "not entitled" is the safe
    // direction if it does.
    return false;
  }

  const expiresAt = new Date(user.subscription_expires_at);
  return expiresAt > new Date();
}

/**
 * Get user's current plan tier
 * Falls back to 'free' if not subscribed
 *
 * @param user User object
 * @returns Current plan tier
 */
export function getUserPlan(user: Partial<User>): SubscriptionPlan {
  if (isSubscriptionActive(user)) {
    return user.subscription_plan || 'free';
  }
  return 'free';
}

/**
 * Whether this user's editor must be held to the strict media-reference rules: the
 * convert flow has to require a real file path up front and refuse to post without one.
 *
 * True for DaVinci Resolve and true for an unknown editor. That second case is the whole
 * point of this function and the reason not to write `video_editor === 'davinci_resolve'`
 * inline. An FCP7 clip whose <name>/<pathurl> cannot name a file makes Resolve hang
 * forever on import - no error, no timeout, nothing the user can report - while Premiere
 * and Final Cut mark the clip offline and finish. If we do not know which of those a user
 * is about to hit, the only safe assumption is the one that hangs: being needlessly strict
 * costs them one filled-in field, being wrongly lenient costs them an import they cannot
 * diagnose.
 *
 * The server enforces this regardless (POST /rest/v1/convertxml returns 400
 * missing_media_reference), so this is about warning the user before they get there, not
 * about being the guard.
 *
 * @param user User object with video_editor
 * @returns true if the convert flow must require a usable media reference up front
 */
export function requiresStrictMediaReference(user: Partial<User>): boolean {
  return user.video_editor !== 'premiere' && user.video_editor !== 'final_cut_pro';
}

/**
 * Whether to put the DaVinci Resolve media-path requirement in front of this user before
 * they convert: they are on Resolve and have not acknowledged it yet.
 *
 * Scoped to a confirmed Resolve user, not to requiresStrictMediaReference() - somebody we
 * have never asked should be asked which editor they use, which is a different prompt to
 * an explanation of a Resolve-specific failure. Both can be true of the same session; they
 * are not the same dialog.
 *
 * @param user User object with video_editor and resolve_warning_acknowledged_at
 * @returns true if the Resolve warning still needs acknowledging
 */
export function needsResolveWarning(user: Partial<User>): boolean {
  return user.video_editor === 'davinci_resolve' && !user.resolve_warning_acknowledged_at;
}

/**
 * Check if user has a specific plan or higher
 * @param user User object
 * @param requiredPlan Minimum plan required
 * @returns true if user's plan meets requirement
 */
export function hasPlanLevel(user: Partial<User>, requiredPlan: SubscriptionPlan): boolean {
  const userPlan = getUserPlan(user);

  const planHierarchy: Record<SubscriptionPlan, number> = {
    free: 0,
    pro: 1,
    team: 2,
  };

  return planHierarchy[userPlan] >= planHierarchy[requiredPlan];
}
